import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import '../styles/design-system.css';
import './QRPage.css';

// Pseudo-random QR pattern (same algorithm as mockup)
function paintQR(id, seed) {
  const el = document.getElementById(id);
  if (!el) return;
  let s = seed;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const cells = 15 * 15;
  let html = '';
  for (let i = 0; i < cells; i++) {
    const r = Math.floor(i / 15);
    const c = i % 15;
    const inCorner = (r < 4 && c < 4) || (r < 4 && c > 10) || (r > 10 && c < 4);
    let on;
    if (inCorner) {
      const rr = r < 4 ? r : 14 - r;
      const cc = c < 4 ? c : (c > 10 ? 14 - c : c);
      on = rr === 0 || rr === 3 || cc === 0 || cc === 3 ||
        (rr === 1 && cc === 1) || (rr === 2 && cc === 2) ||
        (rr === 1 && cc === 2) || (rr === 2 && cc === 1);
    } else {
      on = rand() > 0.55;
    }
    const color = on ? (i % 17 === 0 ? '#ff5436' : '#0d0d0f') : 'transparent';
    html += `<div style="aspect-ratio:1;background:${color};border-radius:1px;"></div>`;
  }
  el.innerHTML = html;
}

function QRCard({ linkSlug, scans, seed, idx }) {
  const id = `qr-card-${idx}`;
  useEffect(() => {
    paintQR(id, seed);
  }, [id, seed]);
  return (
    <div className="panel qr-card">
      <div className="qr-canvas-wrap">
        <div id={id} className="qr-canvas" />
      </div>
      <div className="qr-slug">{linkSlug}</div>
      <div className="qr-scans">{scans.toLocaleString()} scans</div>
      <div className="qr-actions">
        <button className="btn-outline" style={{fontSize:11,padding:'5px 10px'}}>PNG</button>
        <button className="btn-outline" style={{fontSize:11,padding:'5px 10px'}}>SVG</button>
      </div>
    </div>
  );
}

export default function QRPage() {
  const { workspace } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspace?.workspace_id) return;
    supabase
      .from('links')
      .select('link_id, slug, click_count')
      .eq('workspace_id', workspace.workspace_id)
      .order('click_count', { ascending: false })
      .limit(8)
      .then(({ data }) => {
        setLinks(data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [workspace?.workspace_id]);

  const SEEDS = [12345, 67890, 24680, 13579, 54321, 98765, 11111, 22222];
  const displayLinks = links.length > 0 ? links : [
    { link_id: '1', slug: 'spring-26', click_count: 3142 },
    { link_id: '2', slug: 'webinar-may', click_count: 1827 },
    { link_id: '3', slug: 'aws-talk', click_count: 934 },
    { link_id: '4', slug: 'kubecon', click_count: 412 },
  ];

  return (
    <div className="ds-page" data-testid="qr-page">
      <div className="page-header">
        <div>
          <div className="page-label mustard">Library</div>
          <h1 className="page-title">QR <em>codes</em>.</h1>
          <p className="page-sub">Branded, trackable QRs for every link. PNG or SVG export.</p>
        </div>
        <button className="btn-coral">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5v14"/>
          </svg>
          New QR
        </button>
      </div>

      {loading ? (
        <div className="qr-grid">
          {[1,2,3,4].map(i => <div key={i} className="ds-skeleton" style={{height:220}} />)}
        </div>
      ) : (
        <div className="qr-grid">
          {displayLinks.map((link, i) => (
            <QRCard
              key={link.link_id}
              linkSlug={`lfo.to/${link.slug}`}
              scans={link.click_count || 0}
              seed={SEEDS[i % SEEDS.length]}
              idx={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}
